import React, { useContext, useState, useEffect, useRef } from 'react';
import classes from '@/components/Collections/Collection/Collection.module.scss';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { Button } from '@/components';
import t from '@/utils/Language/Collection-translations';
import Collections from '@/utils/api/odas/collection';
import ODRS from '@/utils/api/odrs';
import { resourceHelpers } from '@/utils/helpers';
import Skeleton from 'react-loading-skeleton';
import CategoryEmptyState from './CategoryEmptyState';
import CollectionEmptyState from '@/components/Collections/Collection/CollectionEmptyState';
import ResourceCard from '@/components/Resources/ResourceCard/ResourceCard';
import CollectionCard from '@/components/Collections/CollectionCard/CollectionCard';
import { languageContext } from '@/context/language.context';
import { ACTION_BUTTONS } from '@/components/Resources/ResourceCard/constants';
import TablePagination from '@/components/TablePagination/TablePagination';
import { useRouter } from 'next/router';

const CollectionResources = ({
  collection,
  withCategories = true,
  pageRef,
  hasActiveSearch = false,
  isOwner,
  isEditor,
  activeSearchText,
  removeItemFromCollection,
  addNewCategory,
  onResourceCountChange,
  hideResourceCount = false
}) => {
  const [resultsLoading, setResultsLoading] = useState(true);
  const [oppResults, setOppResults] = useState([]);
  const [orgResults, setOrgResults] = useState([]);
  const [collectionResults, setCollectionResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const router = useRouter();
  const { currentLanguage } = useContext(languageContext);
  const cachedResultsRef = useRef(null);
  const prevHasActiveSearchRef = useRef(false);

  useEffect(() => {
    if (!collection?.fetchable_list_items) return;

    const wasSearching = prevHasActiveSearchRef.current;
    prevHasActiveSearchRef.current = hasActiveSearch;

    if (hasActiveSearch && activeSearchText) {
      // Save pre-search results before the first search so they can be restored when search is canceled
      if (!wasSearching) {
        cachedResultsRef.current = { oppResults, orgResults, collectionResults };
      }
      searchODRS({ withCategories: withCategories });
      searchODAS();
    } else if (wasSearching && cachedResultsRef.current) {
      // Search cleared: restore cached results instantly
      setOppResults(cachedResultsRef.current.oppResults);
      setOrgResults(cachedResultsRef.current.orgResults);
      setCollectionResults(cachedResultsRef.current.collectionResults);
      cachedResultsRef.current = null;
      setResultsLoading(false);
    } else {
      searchODRS({ withCategories: false });
      searchODAS();
    }
  }, [collection?.fetchable_list_items, activeSearchText, hasActiveSearch, currentPage, currentLanguage]);

  useEffect(() => {
    if (onResourceCountChange) {
      onResourceCountChange(getTotalResourceCount());
    }
  }, [oppResults, orgResults, collectionResults]);

  const onPageChange = (selectedPage) => {
    setCurrentPage(selectedPage);
    pageRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const getTotalResourceCount = () => {
    const oppCount = oppResults.data?.paging?.total_count || 0;
    const orgCount = orgResults.data?.paging?.total_count || 0;
    const collCount = collectionResults.data?.pagination?.total_count || 0;
    return oppCount + orgCount + collCount;
  };

  const getCountLabel = () => {
    const total = getTotalResourceCount();
    if (hasActiveSearch) {
      return t('${count} ${resources} found for "${searchTerm}" in ${collectionTitle}', {
        count: total,
        resources: t(total === 1 ? 'resource' : 'resources'),
        searchTerm: activeSearchText,
        collectionTitle: collection?.title
      });
    }
    return `${total} ${t(total === 1 ? 'Resource' : 'Resources')}`;
  };

  const getUnifiedResources = () => {
    const unified = [];

    // Add opportunities
    if (oppResults?.data?.opportunities) {
      oppResults.data.opportunities.forEach((resource) => {
        unified.push({ ...resource, _resourceType: 'opportunities' });
      });
    }

    // Add organizations
    if (orgResults?.data?.organizations) {
      orgResults.data.organizations.forEach((resource) => {
        unified.push({ ...resource, _resourceType: 'organizations' });
      });
    }

    // Add collections
    if (collectionResults?.data?.collections) {
      collectionResults.data.collections.forEach((resource) => {
        unified.push({ ...resource, _resourceType: 'collections' });
      });
    }

    return unified;
  };

  const getUnifiedPagination = () => {
    const oppPaging = oppResults?.data?.paging || { total_count: 0, per_page: 10, current_page: 1 };
    const orgPaging = orgResults?.data?.paging || { total_count: 0, per_page: 10, current_page: 1 };
    const collPaging = collectionResults?.data?.pagination || { total_count: 0, per_page: 10, current_page: 1 };

    // Calculate max page count from all types (matching Angular logic)
    const oppPageCount = Math.ceil(oppPaging.total_count / oppPaging.per_page);
    const orgPageCount = Math.ceil(orgPaging.total_count / orgPaging.per_page);
    const collPageCount = Math.ceil(collPaging.total_count / collPaging.per_page);
    const maxPageCount = Math.max(oppPageCount, orgPageCount, collPageCount);

    // Use the largest total_count (matching Angular line 15)
    const maxTotalCount = Math.max(oppPaging.total_count, orgPaging.total_count, collPaging.total_count);

    return {
      total_count: maxTotalCount,
      total_pages: maxPageCount,
      current_page: currentPage || 1,
      per_page: 10,
      page: currentPage || 1
    };
  };

  const searchODRS = async ({ withCategories }) => {
    let params = {};
    let ids = resourceHelpers.getFetchableIds(collection.fetchable_list_items);
    let opportunityIds = ids['opportunities'];
    let organizationIds = ids['organizations'];

    if (withCategories && collection.category_fetchable_list_items) {
      let categoryIds = resourceHelpers.getFetchableIds(
        (collection.category_fetchable_list_items.opportunities || []).concat(collection.category_fetchable_list_items.organizations || [])
      );
      opportunityIds = opportunityIds.concat(categoryIds['opportunities']);
      organizationIds = organizationIds.concat(categoryIds['organizations']);
    }
    params['query[text]'] = activeSearchText;
    params['query[lat]'] = collection.lat;
    params['query[long]'] = collection.long;
    params['query[tags_plus]'] = true;
    params.page = currentPage;
    params.locale = currentLanguage;
    setResultsLoading(true);

    if (opportunityIds.length) {
      params.ids = opportunityIds.join();
      let oppResp = await ODRS.search('opportunities', params);
      setOppResults(oppResp);
      // TODO
      // getUtilizationsMembers(opportunityIds);
    } else {
      setOppResults(false);
    }

    if (organizationIds.length) {
      params.ids = organizationIds.join();
      let orgResp = await ODRS.search('organizations', params);
      setOrgResults(orgResp);
    } else {
      setOrgResults(false);
    }

    setResultsLoading(false);
  };

  const searchODAS = async () => {
    // send ODAS search for Collections with list of Collection ids in this collection
    let params = {};
    let ids = resourceHelpers.getFetchableIds(collection.fetchable_list_items);
    let collectionIds = ids['collections'];
    params['query[text]'] = activeSearchText;
    params['query[lat]'] = collection.lat;
    params['query[long]'] = collection.long;
    params.page = currentPage;
    if (collectionIds.length) {
      params.ids = collectionIds.join();
      const resp = await Collections.searchCollections(params);
      setCollectionResults(resp);
    } else {
      setCollectionResults(false);
    }
  };

  return (
    <>
      <Row className={classes.resourceResultsContainer} ref={pageRef}>
        <Col className={classes.flushDesktop}>
          {!hideResourceCount && !resultsLoading && (hasActiveSearch || getTotalResourceCount() > 0) && (
            <Row className={classes.resourcesLabel}>
              <Col style={{ paddingLeft: '12px' }}>
                <label className={[classes.formLabel, 'form-label'].join(' ')}>{getCountLabel()}</label>
              </Col>
            </Row>
          )}
          <Row>
            {resultsLoading && (
              <>
                <Skeleton className={'mb-3'} height={300} highlightColor={'#ffffff'} />
                <Skeleton className={'mb-3'} height={300} highlightColor={'#ffffff'} />
                <Skeleton height={300} highlightColor={'#ffffff'} />
              </>
            )}
            {!resultsLoading && getTotalResourceCount() > 0 && (
              <div className={classes.resourcesList}>
                {getUnifiedResources().map((resource) => {
                  if (resource._resourceType === 'opportunities' || resource._resourceType === 'organizations') {
                    return (
                      <ResourceCard
                        key={`${resource._resourceType}-${resource.id}`}
                        resource={resource}
                        language={currentLanguage}
                        showResourceRemoveFromCollectionLink={isOwner || isEditor}
                        showSuggestEdits={false}
                        includeFilterNote={false}
                        actionButtons={[ACTION_BUTTONS.ACTION, ACTION_BUTTONS.BOOKMARK, ACTION_BUTTONS.ADD_TO_COLLECTION]}
                        removeItemFromCollection={() => removeItemFromCollection(resource, resource._resourceType)}
                      />
                    );
                  } else if (resource._resourceType === 'collections') {
                    return (
                      <CollectionCard
                        key={`${resource._resourceType}-${resource.id}`}
                        collection={resource}
                        removeItemFromCollection={isOwner || isEditor ? () => removeItemFromCollection(resource, resource._resourceType) : undefined}
                      />
                    );
                  }
                  return null;
                })}
                {getUnifiedPagination().total_count > 10 && (
                  <Row className="align-end pe-0 mt-2 ms-0">
                    <TablePagination pagination={getUnifiedPagination()} onPageChange={onPageChange} containerClass={classes.paginationContainer} />
                  </Row>
                )}
              </div>
            )}
            {!resultsLoading && hasActiveSearch && getTotalResourceCount() === 0 && (
              <>
                <Card className={classes.blankSearchResult}>
                  <Row>
                    <Col className={classes.blankSearchResultHeader}>
                      <span>{t('We couldn’t find any resources for your search')}</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col className={classes.blankSearchResultBody}>
                      {t(
                        'There aren’t any resources in this ${context} that match your search. Try searching for something else, or using the One Degree search to find what you’re looking for.',
                        { context: withCategories ? 'collection' : 'category' }
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className={classes.blankSearchResultBtn}>
                      <Button
                        dataCy="button-add-client"
                        type="primary"
                        icon={{ style: 'fas', font: 'search' }}
                        text={t('Search One Degree')}
                        additionalClasses={classes.searchOneDegreeBtn}
                        onButtonClick={() => router.push('/browse')}
                      />
                    </Col>
                  </Row>
                  <Card.Body></Card.Body>
                </Card>
              </>
            )}
            {!resultsLoading && !hasActiveSearch && getTotalResourceCount() === 0 && (
              <>
                {withCategories ? (
                  <CollectionEmptyState addNewCategory={addNewCategory} hasCategories={collection?.categories?.length > 0} />
                ) : (
                  <CategoryEmptyState />
                )}
              </>
            )}
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default CollectionResources;
